import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap } from "lucide-react";

/**
 * Tasteful lead capture: fires once per session, either on exit-intent
 * (mouse leaving toward the tab bar, desktop only) or after 50s on page.
 */
export default function ExitIntent() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let done = false;
    try { done = sessionStorage.getItem("jm_exit_shown") === "1"; } catch { /* ignore */ }
    if (done) return;
    const fire = () => {
      setShow(true);
      try { sessionStorage.setItem("jm_exit_shown", "1"); } catch { /* ignore */ }
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 8) fire();
    };
    const timer = setTimeout(fire, 50000);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={() => setShow(false)}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="relative bg-background border border-border max-w-lg w-full p-8 md:p-10 soft-shadow-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-intent-modal"
            role="dialog" aria-modal="true" aria-label="Before you go"
          >
            <button onClick={() => setShow(false)} aria-label="Close" data-testid="exit-intent-close"
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
            <p className="overline text-primary mb-4">Before you go…</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">Want to know what your website could cost?</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Build your package in about 60 seconds and get an instant estimate — no sales call, no obligation.
            </p>
            <button
              data-testid="exit-intent-cta"
              onClick={() => { setShow(false); navigate("/pricing"); }}
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary/90 active:scale-95 transition w-full justify-center"
            >
              <Zap className="w-4 h-4" /> Get Your Free Estimate
            </button>
            <button onClick={() => setShow(false)} data-testid="exit-intent-dismiss"
              className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors">
              No thanks, I'm just browsing
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
